import { Box, Card, Checkbox, Typography } from '@mui/material';
import { ImageBox, IconButton } from '@tix-ticketing/ui-components';
import { CrossIcon } from '@tix-ticketing/ui-icons/outline';
import { pinToMinFontSize } from '@tix-ticketing/ui-theme';
import { useIntl } from 'react-intl';
import { staticCardSx } from 'components/common/cart/static-card-sx';
import { DiscountedPrice } from 'components/common/price/discounted-price';
import { useIsMobile } from 'lib/hooks/use-is-mobile';
interface CartExtraOrderItemProps {
  imageUrl: string;
  header: React.ReactNode;
  subtitle?: React.ReactNode;
  footer?: React.ReactNode;
  footerDescription?: React.ReactNode;
  price: number;
  originalPrice?: number | null;
  inAside?: boolean;
  onRemove?: () => void;
  /**
   * Renders a leading checkbox. Set only while the cart is running a
   * select-items-to-pay selection over the order.
   */
  onToggleSelect?: () => void;
  selected?: boolean;
  selectLabel?: string;
}
export const CartExtraOrderItem = ({
  imageUrl,
  header,
  subtitle,
  footer,
  footerDescription,
  price,
  originalPrice,
  inAside = false,
  onRemove,
  onToggleSelect,
  selected = false,
  selectLabel
}: CartExtraOrderItemProps) => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const compact = isMobile || inAside;
  const selectionCheckbox = onToggleSelect && <Checkbox checked={selected} onChange={onToggleSelect} slotProps={{
    input: {
      'aria-label': selectLabel
    }
  }} sx={{
    flexShrink: 0
  }} />;
  if (compact) {
    return <Card variant="outlined" sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 2,
      p: 2,
      position: 'relative',
      ...staticCardSx
    }}>
        <Box sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start'
      }}>
          {selectionCheckbox}
          <ImageBox imageUrl={imageUrl} altText={header?.toString() || ''} sx={{
          width: 64,
          height: 64
        }} />
          <Box sx={{
          flex: 1
        }}>
            <Typography variant="h6" sx={theme => ({
            fontSize: pinToMinFontSize(theme.typography.h6.fontSize)
          })}>
              {header}
            </Typography>
            {subtitle && <Typography variant="body3" sx={{
            color: 'text.secondary',
            mt: 0.5
          }}>
                {subtitle}
              </Typography>}
            {!footer && !footerDescription && <DiscountedPrice price={price} originalPrice={originalPrice} variant="body2Bold" />}
          </Box>
        </Box>
        {(footer || footerDescription) && <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2
      }}>
            <Box>
              {footer && <Typography variant="body2">{footer}</Typography>}
              {footerDescription && <Typography variant="body2">{footerDescription}</Typography>}
            </Box>
            <DiscountedPrice price={price} originalPrice={originalPrice} variant="body2Bold" />
          </Box>}
        {onRemove && <IconButton color="monochrome" variant="text" size="small" onClick={onRemove} sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        color: 'action.active'
      }} label={intl.formatMessage({
        id: "common.remove",
        defaultMessage: [{
          "type": 0,
          "value": "Remove"
        }]
      })}>
            <CrossIcon />
          </IconButton>}
      </Card>;
  }
  return <Card variant="outlined" sx={{
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    p: 3,
    ...staticCardSx
  }}>
      {selectionCheckbox}
      <Box sx={{
      display: 'flex',
      gap: 3,
      alignItems: 'center',
      flex: 1
    }}>
        <ImageBox imageUrl={imageUrl} altText={header?.toString() || ''} sx={{
        width: 80,
        height: 80
      }} />
        <Box sx={{
        flex: 1
      }}>
          <Typography variant="h6" sx={{
          fontWeight: 700
        }}>
            {header}
          </Typography>
          {subtitle && <Typography variant="body3" sx={{
          color: 'text.secondary',
          mt: 0.5
        }}>
              {subtitle}
            </Typography>}
          {footer && <Typography variant="body2" sx={{
          mt: 1
        }}>
              {footer}
            </Typography>}
          {footerDescription && <Typography variant="body2" sx={{
          mt: footer ? 0 : 1
        }}>
              {footerDescription}
            </Typography>}
        </Box>
      </Box>
      <DiscountedPrice price={price} originalPrice={originalPrice} variant="body1Bold" />
      {onRemove && <IconButton color="monochrome" size="small" onClick={onRemove} sx={{
      color: 'action.active'
    }} label={intl.formatMessage({
      id: "common.remove",
      defaultMessage: [{
        "type": 0,
        "value": "Remove"
      }]
    })}>
          <CrossIcon />
        </IconButton>}
    </Card>;
};